import { useState, useEffect } from "react";
import { Box, CircularProgress, Typography, useTheme, Divider, Card, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import TokenIcon from "@mui/icons-material/Token";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import BurnCounter from "./BurnCounter";
import axios from "axios";

const TokenStats = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenData, setTokenData] = useState({
    total_supply: 0,
    total_staked: 0,
    circulating_supply: 0,
    burned: 0,
    minted: 0,
    max_supply: 3000000000 // Maximum SPS supply is 3 billion
  });

  // Function to format large numbers
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    
    // Format to millions or billions with suffix
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else {
      return value.toLocaleString();
    }
  };

  // Calculate percentages for progress circles
  const calculateProgress = (value, total) => {
    if (!value || !total) return 0;
    const progress = value / total;
    return progress > 1 ? 1 : progress; // Ensure we don't exceed 1 (100%)
  };

  // Fetch token data from API
  const fetchTokenData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://splinterlands-validator-api.splinterlands.com/extensions/tokens/SPS/supply"
      );
      
      if (response.data) {
        const data = response.data;
        setTokenData({
          total_supply: data.total_supply,
          total_staked: data.total_staked,
          circulating_supply: data.circulating_supply,
          burned: data.burned,
          minted: data.minted,
          max_supply: 3000000000 // Maximum SPS supply is 3 billion
        });
        setError(null);
      }
    } catch (err) {
      console.error("Failed to fetch token data:", err);
      setError("Failed to load SPS token data");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchTokenData();
    
    // Set up interval to refresh data every minute
    const intervalId = setInterval(fetchTokenData, 60000);
    
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
      pb={isMobile ? 2 : 0}
    >
      {/* Highlighted Burned Tokens Card with animated counter */}
      <Card 
        sx={{ 
          backgroundColor: colors.primary[400], 
          p: 3,
          mb: 3,
          borderRadius: 2,
          boxShadow: 3,
          border: `1px solid ${colors.redAccent[700]}`,
          position: "relative",
          overflow: "hidden",
          height: "auto"
        }}
      >
        {/* Animated fire background effect */}
        <Box 
          sx={{ 
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: `linear-gradient(${colors.redAccent[700]}, transparent)`,
            zIndex: 0
          }}
        />
        
        {/* Content */}
        <Box position="relative" zIndex={1}>
          <Box display="flex" alignItems="center" mb={1}>
            <LocalFireDepartmentIcon sx={{ color: colors.redAccent[500], fontSize: 28, mr: 1 }} />
            <Typography variant="h5" fontWeight="bold" color={colors.redAccent[500]}>
              Burned SPS
            </Typography>
          </Box>
          <Divider sx={{ mb: 2, borderColor: colors.redAccent[700], opacity: 0.3 }} />
          
          {/* Animated Counter Component */}
          <BurnCounter />
          
          {/* Percentage of minted */}
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 2, 
              textAlign: "center",
              color: colors.grey[300]
            }}
          >
            {((tokenData.burned / tokenData.minted) * 100).toFixed(2)}% burned of total supply
          </Typography>
        </Box>
      </Card>
      
      {/* Other Stats - Compact layout */}
      <Box my={isMobile ? 2.5 : 2} px={isMobile ? 0.5 : 0}>
        <Box mb={2.5}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Box display="flex" alignItems="center">
              <TokenIcon sx={{ color: colors.greenAccent[600], fontSize: "22px", mr: 1 }} />
              <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                {formatNumber(tokenData.total_supply)}
              </Typography>
            </Box>
            <Box sx={{ width: '40px', height: '40px' }}>
              <Box
                sx={{
                  background: `radial-gradient(${colors.primary[400]} 55%, transparent 56%),
                      conic-gradient(transparent 0deg ${tokenData.total_supply / tokenData.max_supply * 360}deg, ${colors.blueAccent[500]} ${tokenData.total_supply / tokenData.max_supply * 360}deg 360deg),
                      ${colors.greenAccent[500]}`,
                  borderRadius: "50%",
                  width: '40px',
                  height: '40px',
                }}
              />
            </Box>
          </Box>
          <Typography variant="body1" color={colors.greenAccent[500]}>
            Total Supply
          </Typography>
          <Typography variant="body2" color={colors.greenAccent[600]} sx={{ fontStyle: "italic" }}>
            {((tokenData.total_supply / tokenData.max_supply) * 100).toFixed(1)}% of Max
          </Typography>
        </Box>
        
        <Divider sx={{ my: isMobile ? 2.5 : 2, borderColor: colors.primary[600] }} />
        
        <Box mb={isMobile ? 3 : 0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Box display="flex" alignItems="center">
              <TokenIcon sx={{ color: colors.blueAccent[600], fontSize: "22px", mr: 1 }} />
              <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                {formatNumber(tokenData.total_staked)}
              </Typography>
            </Box>
            <Box sx={{ width: '40px', height: '40px' }}>
              <Box
                sx={{
                  background: `radial-gradient(${colors.primary[400]} 55%, transparent 56%),
                      conic-gradient(transparent 0deg ${tokenData.total_staked / tokenData.total_supply * 360}deg, ${colors.blueAccent[500]} ${tokenData.total_staked / tokenData.total_supply * 360}deg 360deg),
                      ${colors.blueAccent[500]}`,
                  borderRadius: "50%",
                  width: '40px',
                  height: '40px',
                }}
              />
            </Box>
          </Box>
          <Typography variant="body1" color={colors.blueAccent[500]}>
            Total Staked
          </Typography>
          <Typography variant="body2" color={colors.blueAccent[600]} sx={{ fontStyle: "italic", mb: isMobile ? 1 : 0 }}>
            {((tokenData.total_staked / tokenData.total_supply) * 100).toFixed(1)}% of Supply
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TokenStats;